import "../NavBar.css";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DesktopNav from "./PageComponents/Navbar/DesktopNav";
import MobileNav from "./PageComponents/Navbar/MobileNav";
import {
  BASE_URL_COMPETITION,
  BASE_URL_MEDIA,
  BASE_URL_METABUDDY,
  BASE_URL_TRAINER,
  CORPORATE_LOGO,
  IS_CORPORATE,
} from "../api/Constants";
import { setCurrentPage } from "./helpers/Helpers";

/**
 * Main Navbar component
 * Controls the overall navigation and state, delegating rendering to desktop and mobile components
 */
const Navbar = () => {
  // State management
  const [isActive, setIsActive] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const metaToken = localStorage.getItem("metatoken");
  const metaUserId = localStorage.getItem("metauserid");
  const userProfile = localStorage.getItem("userProfile");
  const userEmail = localStorage.getItem("metaemail") || "user@example.com";
  const navigate = useNavigate();
  const location = useLocation();
  const [activePage, setActivePage] = useState("/");

  // Paths that don't need to be tracked for navigation state
  const restrictedPaths = [
    "/login",
    "/register",
    "/reset-password",
    "/forgot-password",
    "/login-otp-verify",
    "/get-trial",
    "/verify-otp",
  ];

  // Get logo path
  const logo = IS_CORPORATE
    ? CORPORATE_LOGO
      ? `${BASE_URL_MEDIA}/apis/storage/assets/uploads/${CORPORATE_LOGO}`
      : `${BASE_URL_MEDIA}/apis/storage/competition-dashboard/static/media/logo.png`
    : `${BASE_URL_MEDIA}/apis/storage/competition-dashboard/static/media/logo.png`;

  // Check and update current page URL for navigation highlighting
  const checkCurrentPageUrl = () => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const queryParams = url.search;
    const originWithoutWWW = window.location.origin.replace(/www\./, "");

    const basePath = BASE_URL_COMPETITION.replace(originWithoutWWW, "");
    const extractedPart = url.pathname.replace(basePath, "").trim();

    if (!restrictedPaths.includes(extractedPart)) {
      if (!(extractedPart + queryParams).length > 0) {
        setCurrentPage("/");
      } else {
        setCurrentPage(extractedPart + queryParams);
      }
    }
    setActivePage(extractedPart + queryParams);
  };

  // Update active page on location change
  useEffect(() => {
    checkCurrentPageUrl();
  }, [location]);

  // Set initial active page
  useEffect(() => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const basePath = BASE_URL_COMPETITION.replace(window.location.origin, "");
    const extractedPart = url.pathname.replace(basePath, "").trim();
    setActivePage(extractedPart);
  }, []);

  // Logout handler
  const LogOut = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  // Toggle mobile navigation
  const toggleNav = () => {
    setIsActive(!isActive);
  };

  // Toggle user menu dropdown
  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  // Check user account type for conditional menu items
  const accountType = localStorage.getItem("accountType");
  const canAccess =
    accountType === "corporate_admin" ||
    accountType === "admin" ||
    accountType === "trainer";

  // Define shared menu items with submenu support
  const menuItems = [
    { path: "/", label: "Fitness Challenges" },
    {
      path: "/events",
      label: "Events",
      // submenu: [
      //   { path: "/events?type=upcoming", label: "Upcoming Events" },
      //   { path: "/events?type=past", label: "Past Events" },
      //   { path: "/events?type=featured", label: "Featured Events" },
      // ],
    },
    { path: "/communities", label: "Communities" },
    { path: "/partners", label: "Partners" },
    ...(canAccess
      ? [
          {
            path: `${BASE_URL_TRAINER}/dashboard?token=${metaToken || ""}&user_id=${metaUserId || 0}`,
            label: "Fitness Programs",
            isExternal: true,
            // submenu: [
            //   {
            //     path: `${BASE_URL_TRAINER}/programs/beginner${
            //       metaToken ? "?token=" + metaToken : ""
            //     }`,
            //     label: "Beginner Programs",
            //     isExternal: true,
            //   },
            //   {
            //     path: `${BASE_URL_TRAINER}/programs/intermediate${
            //       metaToken ? "?token=" + metaToken : ""
            //     }`,
            //     label: "Intermediate Programs",
            //     isExternal: true,
            //   },
            //   {
            //     path: `${BASE_URL_TRAINER}/programs/advanced${
            //       metaToken ? "?token=" + metaToken : ""
            //     }`,
            //     label: "Advanced Programs",
            //     isExternal: true,
            //   },
            // ],
          },
        ]
      : []),
    { path: "/help-center", label: "FAQs" },
    { path: "/contact", label: "Contact us" },
  ];

  return (
    <div className="navbar-wrapper">
      <div className="container-fluid">
        {/* Mobile Navigation */}
        <MobileNav
          menuItems={menuItems}
          activePage={activePage}
          metaToken={metaToken}
          userProfile={userProfile}
          userEmail={userEmail}
          isActive={isActive}
          showUserMenu={showUserMenu}
          toggleNav={toggleNav}
          toggleUserMenu={toggleUserMenu}
          LogOut={LogOut}
          checkCurrentPageUrl={checkCurrentPageUrl}
          logo={logo}
        />

        {/* Desktop Navigation */}
        <DesktopNav
          menuItems={menuItems}
          activePage={activePage}
          metaToken={metaToken}
          userProfile={userProfile}
          userEmail={userEmail}
          showUserMenu={showUserMenu}
          toggleUserMenu={toggleUserMenu}
          LogOut={LogOut}
          checkCurrentPageUrl={checkCurrentPageUrl}
          logo={logo}
        />
      </div>
    </div>
  );
};

export default Navbar;
